"use client";

import * as React from "react";
import { useMemo } from "react";
import { Button, message } from "antd";
import { GalleryVerticalEnd, SquareTerminal } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { NavMain } from "./nav-main";
import { NavUser } from "./nav-user";
import { TeamSwitcher } from "./team-switcher";
import { useAppBranding } from "../../Hook";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarRail,
  SidebarTrigger,
} from "../ui/sidebar";
import { useAuthChecker } from "../../Context";
import { useUserFeaturesQuery } from "../../Services";
import { useClearRuntimeCacheMutation } from "../../Services";

interface SubFeature {
  id: string;
  code: string;
  name: string;
  icon?: string;
  is_enabled?: number;
}

interface Feature {
  id: string;
  feature_id?: number | null;
  feature_code: string;
  feature_name: string;
  feature_icon?: string;
  sub_features?: SubFeature[];
}

const mapFeaturesToSidebarItems = (featuresList: Feature[]) => {
  const enabledCodes: string[] = [];

  const items = featuresList.map((feature) => {
    const hasSub = feature.sub_features && feature.sub_features.length > 0;

    const category = {
      name: feature.feature_name,
      icon: feature.feature_icon || "tag",
    };

    const features = hasSub
      ? feature.sub_features!.map((sub) => {
          if (sub.is_enabled) {
            enabledCodes.push(sub.code);
          }
          return {
            id: sub.id,
            label: sub.name,
            icon: sub.icon || "tag",
            navigation: sub.code,
            is_show_navigation: 1,
            is_locked: sub.is_enabled ? 0 : 1,
          };
        })
      : [
          {
            id: feature.id,
            label: feature.feature_name,
            icon: feature.feature_icon || "tag",
            navigation: feature.feature_code,
            is_show_navigation: 1,
            is_locked: 0,
          },
        ];

    return { category, features };
  });

  localStorage.setItem("enabled_features", JSON.stringify(enabledCodes));
  return items;
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, navigationPath, isAdmin } = useAuthChecker();
  const { appName } = useAppBranding();
  const queryClient = useQueryClient();
  const clearRuntimeCacheMutation = useClearRuntimeCacheMutation();
  const featuresQuery = useUserFeaturesQuery();

  const sidebarTeams = [
    {
      name: appName,
      logo: GalleryVerticalEnd,
      plan: "Enterprise",
    },
  ];

  const sidebarItems = useMemo(() => {
    if (!featuresQuery.data) return [];
    return mapFeaturesToSidebarItems(featuresQuery.data);
  }, [featuresQuery.data]);

  const handleClearCache = () => {
    clearRuntimeCacheMutation.mutate(["api"], {
      onSuccess: (res) => {
        if (res?.success) {
          message.success(res.message || "Application cache cleared.");
          queryClient.invalidateQueries();
        } else {
          message.error(res?.message || "Failed to clear cache.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "Failed to clear cache.");
      },
    });
  };

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <TeamSwitcher teams={sidebarTeams} />
        </SidebarHeader>

        <SidebarContent>
          <NavMain items={sidebarItems} navigationPath={navigationPath} />
        </SidebarContent>

        <SidebarFooter>
          {isAdmin && (
            <Button
              size="middle"
              block
              loading={clearRuntimeCacheMutation.isPending}
              onClick={handleClearCache}
              className="h-9 text-xs font-medium"
            >
              Clear Cache
            </Button>
          )}

          <SidebarInset className="lg:block sm:hidden">
            <div className="flex justify-end">
              <SidebarTrigger className="p-2 bg-gray-800 dark:bg-gray-100 dark:text-black text-white rounded-full">
                <SquareTerminal />
              </SidebarTrigger>
            </div>
          </SidebarInset>
          <NavUser user={user} />
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <div className="lg:hidden absolute top-4 left-4 z-10">
        <SidebarInset>
          <div className="flex justify-end">
            <SidebarTrigger className="p-2 bg-gray-800 text-white rounded-full">
              <SquareTerminal />
            </SidebarTrigger>
          </div>
        </SidebarInset>
      </div>
    </>
  );
}
